import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ProtectedRoute } from './components/ProtectedRoute';

// Auth Pages
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';

// Onboarding
import Onboarding from './components/onboarding/Onboarding';

// Student Dashboard & Pages
import StudentDashboard from './components/dashboard/StudentDashboard';
import ExploreServices from './pages/student/ExploreServices';
import TrackServices from './pages/student/TrackServices';
import ServiceDetailPage from './pages/student/ServiceDetailPage';
import ServiceLandingPage from './pages/student/ServiceLandingPage';
import MyProfile from './pages/student/MyProfile';
import AccountSettings from './pages/student/AccountSettings';
import HelpSupport from './pages/student/HelpSupport';
import StudentAdmissions from './pages/student/StudentAdmissions';
import SubscriptionsPage from './pages/student/SubscriptionsPage';
import PaymentHistoryPage from './pages/student/PaymentHistoryPage';
import MyTasksPage from './pages/student/MyTasksPage';
import MessagesPage from './pages/student/MessagesPage';
import DocumentsPage from './pages/student/DocumentsPage';
import NotificationsPage from './pages/student/NotificationsPage';
import OffersPage from './pages/student/OffersPage';

// Super Admin Dashboard & Pages
import SuperAdminDashboard from './components/admin/SuperAdminDashboard';
import ManageStudents from './pages/admin/ManageStudents';
import ManageCounselors from './pages/admin/ManageCounselors';
import CounselorDetail from './pages/admin/CounselorDetail';
import ManageAgents from './pages/admin/ManageAgents';
import AgentDetail from './pages/admin/AgentDetail';
import ManageRepresentatives from './pages/admin/ManageRepresentatives';
import RepresentativeDetail from './pages/admin/RepresentativeDetail';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminAssignments from './pages/admin/AdminAssignments';
import AdminServiceRequests from './pages/admin/AdminServiceRequests';
import AdminServiceManagement from './pages/admin/AdminServiceManagement';
import AdminPaymentRequests from './pages/admin/AdminPaymentRequests';
import AdminSettings from './pages/admin/AdminSettings';
import Reports from './pages/admin/Reports';
import AdminAgentRequests from './pages/admin/AdminAgentRequests';
import AdminStudentDetails from './pages/admin/AdminStudentDetails';
import AdminAdmissions from './pages/admin/AdminAdmissions';
import AdminAdmissionDetails from './pages/admin/AdminAdmissionDetails';
import CampaignLeadsPage from './pages/admin/CampaignLeads';

// Counselor Dashboard — REUSES Agent pages (role-aware via AgentLayout)
// The Agent pages detect counselor role and adjust branding + API queries

// Representative Dashboard & Pages (Level 1 & 2)
import RepDashboardMain from './pages/representative/RepDashboardMain';
import RepStudents from './pages/representative/RepStudents';
import RepServiceRequests from './pages/representative/RepServiceRequests';
import RepCommissions from './pages/representative/RepCommissions';
import RepMessages from './pages/representative/RepMessages';

// Partner Dashboard & Pages (Level 3)
import PartnerDashboardMain from './pages/partner/PartnerDashboardMain';
import PartnerStudents from './pages/partner/PartnerStudents';
import PartnerAddStudent from './pages/partner/PartnerAddStudent';
import PartnerServiceRequests from './pages/partner/PartnerServiceRequests';
import PartnerServiceTracking from './pages/partner/PartnerServiceTracking';
import PartnerServices from './pages/partner/PartnerServices';
import PartnerTasks from './pages/partner/PartnerTasks';
import PartnerCommissions from './pages/partner/PartnerCommissions';
import PartnerMessages from './pages/partner/PartnerMessages';

// Agent Dashboard & Pages
import AgentDashboardMain from './pages/agent/AgentDashboardMain';
import AgentApplications from './pages/agent/AgentApplications';
import AgentExploreProducts from './pages/agent/AgentExploreProducts';
import AgentLeads from './pages/agent/AgentLeads';
import AgentEarnings from './pages/agent/AgentEarnings';
import AgentVASServices from './pages/agent/AgentVASServices';
import AgentAdmissions from './pages/agent/AgentAdmissions';
import AgentCourseFinder from './pages/agent/AgentCourseFinder';
import AgentCommission from './pages/agent/AgentCommission';
import AgentStudents from './pages/agent/AgentStudents';
import AgentStudentDetails from './pages/agent/AgentStudentDetails';
import AgentServiceRequests from './pages/agent/AgentServiceRequests';
import AgentServiceRequestDetails from './pages/agent/AgentServiceRequestDetails';
import AgentTasks from './pages/agent/AgentTasks';
import AgentAdmissionDetails from './pages/agent/AgentAdmissionDetails';

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />

            {/* Onboarding */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <Onboarding />
                </ProtectedRoute>
              }
            />

            {/* ==================== STUDENT ROUTES ==================== */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/services"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <ExploreServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/track"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <TrackServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/service/:serviceRequestId"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <ServiceDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/services/:serviceSlug"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <ServiceLandingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/subscriptions"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <SubscriptionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/payments"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <PaymentHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <MyProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/help"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <HelpSupport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admissions"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentAdmissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/tasks"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <MyTasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/messages"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/documents"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <DocumentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/notifications"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/offers"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <OffersPage />
                </ProtectedRoute>
              }
            />

            {/* ==================== SUPER ADMIN ROUTES ==================== */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/students"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <ManageStudents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/students/:studentId"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminStudentDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/counselors"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <ManageCounselors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/counselors/:counselorId"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <CounselorDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/agents"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <ManageAgents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/agents/:agentId"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AgentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/representatives"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <ManageRepresentatives />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/representatives/:repId"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <RepresentativeDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/assignments"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminAssignments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/service-requests"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminServiceRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/service-management/:serviceRequestId"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminServiceManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payment-requests"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminPaymentRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/agent-requests"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminAgentRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports/campaign-leads"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <CampaignLeadsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/admissions"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminAdmissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/admissions/:id"
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <AdminAdmissionDetails />
                </ProtectedRoute>
              }
            />

            {/* ==================== COUNSELOR ROUTES ==================== */}
            {/* Counselor reuses Agent page components — AgentLayout detects role */}
            <Route path="/counselor" element={<ProtectedRoute allowedRoles={['counselor']}><AgentDashboardMain /></ProtectedRoute>} />
            <Route path="/counselor/students" element={<ProtectedRoute allowedRoles={['counselor']}><AgentStudents /></ProtectedRoute>} />
            <Route path="/counselor/students/:studentId" element={<ProtectedRoute allowedRoles={['counselor']}><AgentStudentDetails /></ProtectedRoute>} />
            <Route path="/counselor/service-requests" element={<ProtectedRoute allowedRoles={['counselor']}><AgentServiceRequests /></ProtectedRoute>} />
            <Route path="/counselor/service-requests/:id" element={<ProtectedRoute allowedRoles={['counselor']}><AgentServiceRequestDetails /></ProtectedRoute>} />
            <Route path="/counselor/tasks" element={<ProtectedRoute allowedRoles={['counselor']}><AgentTasks /></ProtectedRoute>} />
            <Route path="/counselor/explore" element={<ProtectedRoute allowedRoles={['counselor']}><AgentExploreProducts /></ProtectedRoute>} />
            <Route path="/counselor/vas" element={<ProtectedRoute allowedRoles={['counselor']}><AgentVASServices /></ProtectedRoute>} />
            <Route path="/counselor/admissions" element={<ProtectedRoute allowedRoles={['counselor']}><AgentAdmissions /></ProtectedRoute>} />
            <Route path="/counselor/admissions/:id" element={<ProtectedRoute allowedRoles={['counselor']}><AgentAdmissionDetails /></ProtectedRoute>} />
            <Route path="/counselor/applications" element={<ProtectedRoute allowedRoles={['counselor']}><AgentApplications /></ProtectedRoute>} />
            <Route path="/counselor/courses" element={<ProtectedRoute allowedRoles={['counselor']}><AgentCourseFinder /></ProtectedRoute>} />
            <Route path="/counselor/commission" element={<ProtectedRoute allowedRoles={['counselor']}><AgentCommission /></ProtectedRoute>} />
            <Route path="/counselor/earnings" element={<ProtectedRoute allowedRoles={['counselor']}><AgentEarnings /></ProtectedRoute>} />

            {/* ==================== AGENT ROUTES ==================== */}
            <Route
              path="/agent"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentDashboardMain />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/explore"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentExploreProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/students"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentStudents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/students/:studentId"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentStudentDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/leads"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentLeads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/vas"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentVASServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/admissions"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentAdmissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/admissions/:id"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentAdmissionDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/applications"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentApplications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/courses"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentCourseFinder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/commission"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentCommission />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/earnings"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentEarnings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/service-requests"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentServiceRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/service-requests/:id"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentServiceRequestDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent/tasks"
              element={
                <ProtectedRoute allowedRoles={['agent']}>
                  <AgentTasks />
                </ProtectedRoute>
              }
            />

            {/* ==================== REPRESENTATIVE ROUTES (Level 1 & 2) ==================== */}
            <Route
              path="/representative"
              element={
                <ProtectedRoute allowedRoles={['rep1', 'rep2']}>
                  <RepDashboardMain />
                </ProtectedRoute>
              }
            />
            <Route
              path="/representative/students"
              element={
                <ProtectedRoute allowedRoles={['rep1', 'rep2']}>
                  <RepStudents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/representative/service-requests"
              element={
                <ProtectedRoute allowedRoles={['rep1', 'rep2']}>
                  <RepServiceRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/representative/commissions"
              element={
                <ProtectedRoute allowedRoles={['rep1', 'rep2']}>
                  <RepCommissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/representative/messages"
              element={
                <ProtectedRoute allowedRoles={['rep1', 'rep2']}>
                  <RepMessages />
                </ProtectedRoute>
              }
            />

            {/* ==================== PARTNER ROUTES (Level 3) ==================== */}
            <Route
              path="/partner"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerDashboardMain />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/students"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerStudents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/add-student"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerAddStudent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/service-requests"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerServiceRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/service-requests/:id"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerServiceTracking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/services"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerServices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/tasks"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/commissions"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerCommissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner/messages"
              element={
                <ProtectedRoute allowedRoles={['rep3']}>
                  <PartnerMessages />
                </ProtectedRoute>
              }
            />

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>

          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={4000}
            visibleToasts={3}
            toastOptions={{
              style: { maxWidth: '400px' },
            }}
          />
        </BrowserRouter>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
